import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Badge, Grid, Stack, Tooltip } from "@mui/material";
import AvatarWithInitials from "../AvatarWithInitials";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";

// import useRoles from "../../../hooks/custom/useRoles";
import { setDrawer, setUnreadMessagesCount } from "../../../redux/features/basicSlice";
import { resetUserSettings } from "../../../redux/features/userSettingsSlice";
import { resetUserDetails } from "../../../redux/features/userSlice";
import { removeToken } from "../../../storage/user";
import { SignInRoute } from "../../route/routes";
import useMenuData from "./sidebarLinks";
import { AuthorizationActions } from "../../api/RequestUtil";
import { generateToken } from "../../../notifications/firebase";
import { fetchUnreadMessagesCount } from "../../api/messagesApi";

const drawerWidth = 280;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(72px + 1px)`,
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-around",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

export default function MiniDrawer() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);
  const unreadMessagesCount = useSelector((state) => state.basic.unreadMessagesCount);

  // const { isManager } = useRoles();

  const [open, setOpen] = React.useState(true);
  const [openItems, setOpenItems] = React.useState({});
  const [showScrollIndicator, setShowScrollIndicator] = React.useState(false);
  const scrollContainerRef = React.useRef(null);

  const { firstName, lastName, email, profileImage } = user;

  const { menuItems, helpItem } = useMenuData();

  // Fetch unread messages count on mount and periodically
  React.useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const count = await fetchUnreadMessagesCount();
        dispatch(setUnreadMessagesCount(count));
      } catch (error) {
        // Silently fail - not critical
      }
    };

    // Initial fetch
    fetchUnreadCount();

    // Refresh every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Check if content is scrollable
  const checkScrollIndicator = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const hasScroll = container.scrollHeight > container.clientHeight;
      const isAtBottom = container.scrollHeight - container.scrollTop === container.clientHeight;
      setShowScrollIndicator(hasScroll && !isAtBottom);
    }
  }, []);

  React.useEffect(() => {
    checkScrollIndicator();
    window.addEventListener("resize", checkScrollIndicator);
    return () => window.removeEventListener("resize", checkScrollIndicator);
  }, [checkScrollIndicator, openItems, open]);

  const handleToggle = (itemText) => {
    setOpenItems((prev) => ({
      ...prev,
      [itemText]: !prev[itemText],
    }));
    setOpen(true);
  };

  const handleDrawer = () => {
    setOpen(!open);
    dispatch(setDrawer(!open));
    if (!open) {
      // When opening drawer, close all nested menus
      setOpenItems({});
    }
  };

  const renderSidebarItems = (items) => (
    <List sx={{ color: theme.palette.common.white }}>
      {items.map((item) => {
        const isSelected = location.pathname === item.path;
        // Add CSS classes for tour targeting
        const getItemClassName = (text) => {
          const classMap = {
            Clients: "clients-section",
            "Program Manager": "programs-section",
            Calendar: "calendar-section",
            "Market Hub": "market-hub-section",
            Admin: "settings-menu",
          };
          return classMap[text] || "";
        };

        return (
          <React.Fragment key={item.text}>
            {item.enableDivider && (
              <Divider
                variant="middle"
                sx={{
                  bgcolor: theme.palette.common.white,
                  marginX: 1,
                }}
              />
            )}
            <ListItem disablePadding className={getItemClassName(item.text)}>
              <ListItemButton
                onClick={() => item.nested && handleToggle(item.text)}
                component={item.externalLink ? "a" : item.path ? Link : "button"}
                to={!item.externalLink ? item.path : undefined}
                href={item.externalLink ? item.path : undefined}
                target={item.externalLink ? "_blank" : undefined}
                rel={item.externalLink ? "noopener noreferrer" : undefined}
                sx={{
                  backgroundColor: item.highlight
                    ? theme.palette.lemon.light
                    : isSelected
                      ? theme.palette.common.white
                      : "transparent",
                  color: item.highlight
                    ? theme.palette.common.black
                    : isSelected
                      ? theme.palette.primary.main
                      : theme.palette.common.white,
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    backgroundColor: item.highlight
                      ? theme.palette.lemon.semiTransparent
                      : isSelected
                        ? theme.palette.common.white
                        : theme.palette.action.hover,
                    transform: item.highlight ? "scale(1.02)" : "none",
                    boxShadow: item.highlight ? "0 4px 12px rgba(187, 242, 71, 0.3)" : "none",
                  },
                  padding: "12px",
                  marginX: "12px",
                  borderRadius: "10px",
                  border: item.highlight ? `1px solid ${theme.palette.lemon.light}` : "none",
                }}
              >
                <Tooltip title={item.text} placement="right">
                  <ListItemIcon>
                    <Badge
                      badgeContent={item.text === "Messages" ? unreadMessagesCount : 0}
                      color="error"
                      invisible={item.text !== "Messages" || unreadMessagesCount === 0}
                      sx={{
                        "& .MuiBadge-badge": {
                          right: -3,
                          top: -3,
                          border: `2px solid ${theme.palette.primary.main}`,
                          padding: "0 4px",
                          minWidth: "18px",
                          height: "18px",
                          fontSize: "0.7rem",
                          fontWeight: 700,
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={item.icon}
                        alt={item.text}
                        height="24px"
                        width="24px"
                        sx={{
                          filter: item.highlight
                            ? "brightness(0) saturate(100%)"
                            : isSelected
                              ? "brightness(0) saturate(100%) invert(17%) sepia(9%) saturate(6598%) hue-rotate(121deg) brightness(97%) contrast(99%)"
                              : "brightness(1)",
                        }}
                      />
                    </Badge>
                  </ListItemIcon>
                </Tooltip>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ sx: { fontWeight: item.highlight || isSelected ? "700" : "400" } }}
                />
                {item.externalLink && open && (
                  <OpenInNewIcon
                    sx={{
                      fontSize: "1rem",
                      color: item.highlight ? theme.palette.common.black : theme.palette.common.white,
                      marginLeft: "4px",
                    }}
                  />
                )}
                {item.nested && open && (
                  <ListItemIcon>
                    {openItems[item.text] ? (
                      <KeyboardArrowUpIcon sx={{ fontSize: "2rem" }} />
                    ) : (
                      <KeyboardArrowDownIcon sx={{ fontSize: "2rem" }} />
                    )}
                  </ListItemIcon>
                )}
              </ListItemButton>
            </ListItem>
            {item.nested && openItems[item.text] && open && renderNestedItems(item.nested)}
          </React.Fragment>
        );
      })}
    </List>
  );

  const renderSidebarContent = () => <Box>{renderSidebarItems(menuItems)}</Box>;

  const renderHelpButton = () => {
    const item = helpItem;
    return (
      <Box>
        <Divider
          variant="middle"
          sx={{
            bgcolor: theme.palette.common.white,
            marginX: 1,
            marginBottom: 0.5,
          }}
        />
        <ListItem disablePadding>
          <ListItemButton
            component="a"
            href={item.path}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              backgroundColor: item.highlight ? theme.palette.lemon.light : "transparent",
              color: item.highlight ? theme.palette.common.black : theme.palette.common.white,
              transition: "all 0.3s ease-in-out",
              display: "flex",
              alignItems: "center",
              "&:hover": {
                backgroundColor: item.highlight ? theme.palette.lemon.semiTransparent : theme.palette.action.hover,
                transform: item.highlight ? "scale(1.02)" : "none",
                boxShadow: item.highlight ? "0 4px 12px rgba(187, 242, 71, 0.3)" : "none",
              },
              paddingTop: "12px",
              paddingBottom: "12px",
              paddingLeft: "12px",
              paddingRight: "12px",
              marginX: "12px",
              marginBottom: "12px",
              borderRadius: "10px",
              border: item.highlight ? `1px solid ${theme.palette.lemon.light}` : "none",
            }}
          >
            <Tooltip title={item.text} placement="right">
              <ListItemIcon sx={{ minWidth: "auto", marginRight: "16px", display: "flex", alignItems: "center" }}>
                <Box
                  component="img"
                  src={item.icon}
                  alt={item.text}
                  height="24px"
                  width="24px"
                  sx={{
                    filter: item.highlight ? "brightness(0) saturate(100%)" : "brightness(1)",
                  }}
                />
              </ListItemIcon>
            </Tooltip>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                sx: {
                  fontWeight: item.highlight ? "700" : "400",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            />
            {open && (
              <OpenInNewIcon
                sx={{
                  fontSize: "1rem",
                  color: item.highlight ? theme.palette.common.black : theme.palette.common.white,
                  marginLeft: "4px",
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </Box>
    );
  };

  const onLogout = async () => {
    const device_id = localStorage.getItem("device_id");
    const fcmToken = localStorage.getItem("fcm_token");
    if (fcmToken && device_id) {
      await destroyNotificationToken(device_id);
    }
    removeToken();
    dispatch(resetUserDetails());
    dispatch(resetUserSettings());
    navigate(SignInRoute);
    setTimeout(() => {
      generateToken();
    }, 2000);
  };

  const destroyNotificationToken = (device_id) => {
    AuthorizationActions.deleteTokenFromServer({ device_id })
      .then(() => {})
      .catch((err) => {
        console.log(err);
      });
  };

  const renderNestedItems = (nestedItems) => (
    <List>
      {nestedItems.map((item) => {
        const isSelected = location.pathname === item.path;
        return (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              sx={{
                backgroundColor: isSelected ? theme.palette.common.white : "transparent",
                color: isSelected ? theme.palette.primary.main : theme.palette.common.white,
                "&:hover": {
                  backgroundColor: isSelected ? theme.palette.common.white : theme.palette.action.hover,
                },
                padding: "12px",
                marginX: "12px",
                borderRadius: "10px",
              }}
            >
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ sx: { marginLeft: "50px", fontWeight: isSelected ? "700" : "400" } }}
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );

  return (
    <React.Fragment>
      <CssBaseline />
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            bgcolor: theme.palette.primary.main,
            color: theme.palette.common.white,
            transition: "width 0.3s",
            height: "100dvh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            "&::-webkit-scrollbar": {
              width: "0px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.white.light,
              borderRadius: "0px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.primary.light,
              borderRadius: "0px",
            },
          },
        }}
      >
        <DrawerHeader>
          {open && (
            <Box
              component="img"
              src="/assets/images/Nativus_coach_logo-invert-stacked.gif"
              alt="Nativus logo"
              width="151px"
              height="68px"
            />
          )}
          <Box
            component="img"
            src="/assets/images/sidebar-left.svg"
            alt="sidebar left"
            height="27px"
            width="30px"
            sx={{ cursor: "pointer" }}
            onClick={handleDrawer}
          />
        </DrawerHeader>
        <Box display="flex" flexDirection="column" sx={{ flex: 1, overflow: "hidden" }}>
          {/* Wrapper for scrollable area with gradient */}
          <Box sx={{ flex: 1, position: "relative", overflow: "hidden" }}>
            <Box
              ref={scrollContainerRef}
              onScroll={checkScrollIndicator}
              sx={{
                height: "100%",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {renderSidebarContent()}
            </Box>
            {/* Scroll indicator gradient - fixed at bottom of scrollable area */}
            {showScrollIndicator && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "80px",
                  background: `linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.5))`,
                  pointerEvents: "none",
                  zIndex: 10,
                }}
              />
            )}
          </Box>
          <Box sx={{ position: "relative", zIndex: 2 }}>
            {open && renderHelpButton()}
            {open && (
              <Box>
                <Grid
                  display="flex"
                  alignItems="center"
                  gap={1}
                  paddingTop={0}
                  paddingBottom={2}
                  paddingLeft={1}
                  paddingRight={3}
                  justifyContent="space-between"
                  component={Link}
                  to="/admin/settings"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "8px",
                    },
                  }}
                >
                  <Grid display="flex" alignItems="center" gap={1} margin={0.5} flexGrow={1}>
                    <AvatarWithInitials
                      name={`${firstName} ${lastName}`}
                      src={profileImage}
                      alt={`${firstName} ${lastName} image`}
                    />
                    <Stack>
                      <Tooltip title={`${firstName ?? ""} ${lastName ?? ""}`} placement="top">
                        <Typography
                          fontSize="14px"
                          color={theme.palette.gray.light}
                          noWrap
                          sx={{
                            display: "block",
                            width: "141px",
                            overflow: "hidden !important",
                            textOverflow: "ellipsis !important",
                            whiteSpace: "nowrap !important",
                            maxWidth: "100%",
                            WebkitBoxOrient: "horizontal",
                            WebkitLineClamp: 1,
                          }}
                        >
                          {`${firstName ?? ""} ${lastName ?? ""}`}
                        </Typography>
                      </Tooltip>
                      <Tooltip title={email || ""} placement="top">
                        <Typography
                          fontSize="14px"
                          color={theme.palette.gray.light}
                          noWrap
                          sx={{
                            display: "block",
                            width: "141px",
                            overflow: "hidden !important",
                            textOverflow: "ellipsis !important",
                            whiteSpace: "nowrap !important",
                            maxWidth: "100%",
                            WebkitBoxOrient: "horizontal",
                            WebkitLineClamp: 1,
                          }}
                        >
                          {email || ""}
                        </Typography>
                      </Tooltip>
                    </Stack>
                  </Grid>
                  <Tooltip title="Sign out" placement="right">
                    <Box
                      component="img"
                      src={"/assets/images/logout.svg"}
                      alt="logout logo"
                      sx={{ cursor: "pointer", width: 24, height: 24 }}
                      onClick={onLogout}
                    />
                  </Tooltip>
                </Grid>
              </Box>
            )}
            {!open && (
              <Tooltip title="Sign out" placement="right">
                <Box
                  component="img"
                  src={"/assets/images/logout.svg"}
                  alt="logout logo"
                  margin={2}
                  sx={{ cursor: "pointer", height: 30, width: 30 }}
                  onClick={onLogout}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      </Drawer>
    </React.Fragment>
  );
}
